import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalManagerContext } from './modal-manager.context';
import { ModalEntry } from './modal-manager.state';
import {
  AlertModalOptions,
  ConfirmModalOptions,
  HookformCompletion,
  HookformModalOptions,
  ModalCompletion,
  ModalManager,
  ModalResult,
  PromiseWithModalId,
} from './modal-manager.types';
import Portal from '../../components/uikit/portal/Portal';
import { AlertModal } from './components/AlertModal';
import { ConfirmModal } from './components/ConfirmModal';
import { HookformModal } from './components/HookformModal';
import { FieldValues } from 'react-hook-form';

const createModalId = () => `global-modal-${crypto.randomUUID()}`;

const withModalId = <TResult,>(modalId: string, promise: Promise<TResult>): PromiseWithModalId<TResult> => {
  return Object.assign(promise, { modalId });
};

export const GlobalModalManager = ({ children }: PropsWithChildren) => {
  const [modalStack, setModalStack] = useState<ModalEntry[]>([]);
  const modalStackRef = useRef(modalStack);

  const addModal = useCallback((modal: ModalEntry) => {
    const nextStack = [...modalStackRef.current, modal];
    modalStackRef.current = nextStack;
    setModalStack(nextStack);
  }, []);

  const removeModal = useCallback((modalId: string) => {
    const nextStack = modalStackRef.current.filter((modal) => modal.modalId !== modalId);
    modalStackRef.current = nextStack;
    setModalStack(nextStack);
  }, []);

  const resolveModal = useCallback(
    (modalId: string, result: ModalCompletion | HookformCompletion<unknown>) => {
      const modal = modalStackRef.current.find((entry) => entry.modalId === modalId);
      if (!modal) return;

      removeModal(modalId);
      modal.resolve(result as never);
    },
    [removeModal],
  );

  const hideModal = useCallback(
    (modalId: string) => {
      resolveModal(modalId, { result: ModalResult.Closed });
    },
    [resolveModal],
  );

  const showAlert = useCallback(
    (options: AlertModalOptions) => {
      const modalId = createModalId();
      const promise = new Promise<ModalCompletion>((resolve) => {
        addModal({ type: 'alert', modalId, options, resolve });
      });

      return withModalId(modalId, promise);
    },
    [addModal],
  );

  const showConfirm = useCallback(
    (options: ConfirmModalOptions) => {
      const modalId = createModalId();
      const promise = new Promise<ModalCompletion>((resolve) => {
        addModal({ type: 'confirm', modalId, options, resolve });
      });

      return withModalId(modalId, promise);
    },
    [addModal],
  );

  const showHookform = useCallback(
    <T extends FieldValues>(options: HookformModalOptions<T>) => {
      const modalId = createModalId();
      const promise = new Promise<HookformCompletion<T>>((resolve) => {
        const entry: ModalEntry = {
          type: 'hookform',
          modalId,
          options: options as HookformModalOptions<FieldValues>,
          resolve: resolve as (result: never) => void,
        };
        addModal(entry);
      });

      return withModalId(modalId, promise);
    },
    [addModal],
  );

  useEffect(
    () => () => {
      modalStackRef.current.forEach((modal) => modal.resolve({ result: ModalResult.Closed } as never));
    },
    [],
  );

  const manager = useMemo<ModalManager>(
    () => ({ showAlert, showConfirm, showHookform, hideModal }),
    [hideModal, showAlert, showConfirm, showHookform],
  );

  return (
    <ModalManagerContext.Provider value={manager}>
      {children}
      {modalStack.length > 0 && (
        <Portal>
          {modalStack.map((entry, stackIndex) => {
            const commonProps = {
              key: entry.modalId,
              stackIndex,
              isTopmost: stackIndex === modalStack.length - 1,
              onResolve: resolveModal,
            };

            switch (entry.type) {
              case 'alert':
                return <AlertModal entry={entry} {...commonProps} />;
              case 'confirm':
                return <ConfirmModal entry={entry} {...commonProps} />;
              case 'hookform':
                return <HookformModal entry={entry} {...commonProps} />;
            }
          })}
        </Portal>
      )}
    </ModalManagerContext.Provider>
  );
};
